import { supabase } from '../lib/supabase';

// Récupérer la configuration du tenant avec résolution par domaine
const getTenantConfig = async () => {
  try {
    // Résolution dynamique du tenant par domaine en premier
    const currentDomain = window.location.hostname;
    console.log('🌐 getTenantConfig - Résolution par domaine:', currentDomain);

    // Tentative de résolution par domaine
    const { data: resolvedData, error: resolveError } = await supabase
      .rpc('resolve_tenant_by_domain', { domain_input: currentDomain });

    if (!resolveError && resolvedData && resolvedData.length > 0) {
      const resolved = resolvedData[0];
      console.log('✅ getTenantConfig - Tenant résolu:', resolved.tenant_id, resolved.tenant_name);

      // Utiliser la configuration fusionnée
      return {
        email: resolved.merged_config?.email || `contact@${currentDomain}`,
        name: resolved.merged_config?.name || resolved.tenant_name,
        logo: resolved.merged_config?.logo || '',
        domain_name: currentDomain,
        config: resolved.merged_config || {},
        ...resolved.merged_config // Pour les configurations supplémentaires
      };
    }

    // Fallback vers VITE_TENANT_ID si la résolution par domaine échoue
    console.log('⚠️ getTenantConfig - Résolution par domaine échouée, fallback vers VITE_TENANT_ID');
    const tenantId = import.meta.env.VITE_TENANT_ID || '1';

    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', tenantId)
      .single();

    if (error) {
      console.error('Erreur lors de la récupération de la configuration du tenant:', error);
      // Retourner des valeurs par défaut en cas d'erreur
      return {
        email: 'contact@example.com',
        name: 'Default Tenant',
        logo: '',
        domain_name: 'example.com',
        config: {}
      };
    }

    return {
      email: data.email || `contact@${data.domain_name}`,
      name: data.name || 'Tenant',
      logo: data.logo || '',
      domain_name: data.domain_name || 'example.com',
      config: data.config || {},
      ...data.config // Pour les configurations supplémentaires
    };
  } catch (err) {
    console.error('Erreur inattendue dans getTenantConfig:', err);
    return {
      email: 'contact@example.com',
      name: 'Default Tenant',
      logo: '',
      domain_name: 'example.com',
      config: {}
    };
  }
};

// Configuration des expéditeurs
const getEmailSenders = async (): Promise<EmailSenders> => {
  const tenant = await getTenantConfig();

  return {
    default: {
      email: tenant.email,
      name: tenant.name
    },
    tickets: {
      email: `billetterie@${tenant.domain_name}`,
      name: `${tenant.name} Billeterie`
    },
    support: {
      email: `support@${tenant.domain_name}`,
      name: `Support ${tenant.name}`
    }
  };
};

// Fonction utilitaire pour obtenir les informations de l'expéditeur
const getSenderInfo = async (senderType?: string): Promise<SenderInfo> => {
  const senders = await getEmailSenders();
  if (!senderType || !(senderType in senders)) {
    return senders.default;
  }
  return senders[senderType as keyof EmailSenders];
};

// Type pour les informations d'expédition
interface SenderInfo {
  email: string;
  name: string;
}

interface EmailSenders {
  default: SenderInfo;
  tickets: SenderInfo;
  support: SenderInfo;
  [key: string]: SenderInfo;
}

interface EmailData {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

interface EmailParams {
  to: string;
  name: string;
  subject: string;
  replyText: string;
  messageId?: number;
  htmlContent?: string;
  tenantId?: string;
  sender?: string; // Peut être 'default', 'tickets', 'support' ou tout autre type défini
}

interface TicketEmailData {
  to: string;
  name: string;
  subject: string;
  eventName: string;
  eventDate: string;
  eventLocation: string;
  ticketType: string;
  ticketPrice: number;
  ticketQuantity: number;
  orderCode: string;
  qrCodes: string[];
  attendeePhone?: string;
  htmlContent?: string;
}

interface OtpEmailData {
  to: string;
  name: string;
  otpCode: string;
}

export const sendContactEmail = async (data: EmailData) => {
  try {
    // Obtenir le domaine actuel
    const currentDomain = window.location.hostname;

    // 1. Enregistrer le message en base de données avec le domaine
    const { error } = await supabase
      .from('contact_messages')
      .insert([{
        ...data,
        domain: currentDomain, // Ajouter le domaine
        created_at: new Date().toISOString()
      }]);

    if (error) throw error;

    // 2. Envoyer une notification email à l'admin
    try {
      // Utiliser le même domaine obtenu précédemment
      console.log('🌐 Résolution du tenant pour notification email, domaine:', currentDomain);

      let tenantConfig;

      // Tentative de résolution par domaine
      const { data: resolvedData, error: resolveError } = await supabase
        .rpc('resolve_tenant_by_domain', { domain_input: currentDomain });

      if (!resolveError && resolvedData && resolvedData.length > 0) {
        const resolved = resolvedData[0];
        console.log('✅ Tenant résolu pour email:', resolved.tenant_id, resolved.tenant_name);

        // Utiliser la configuration fusionnée
        tenantConfig = {
          name: resolved.merged_config?.name || resolved.tenant_name,
          email: resolved.merged_config?.email || `contact@${currentDomain}`,
          logo: resolved.merged_config?.logo || '',
          domain_name: currentDomain,
          config: resolved.merged_config || {}
        };
      } else {
        // Fallback vers la méthode classique
        console.log('⚠️ Résolution par domaine échouée pour email, fallback vers getTenantConfig');
        tenantConfig = await getTenantConfig();
      }

      // Préparer le contenu de notification pour l'admin
      const adminNotificationHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background-color: #000; padding: 20px; text-align: center;">
            <img src="${tenantConfig.logo}" alt="${tenantConfig.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
            <h2 style="margin: 0; color: #fff;">Nouveau message de contact</h2>
          </div>
          <div style="padding: 20px; background-color: #fff; color: #333;">
            <p>Un nouveau message a été reçu via le formulaire de contact :</p>

            <div style="background-color: #f9f9f9; border-left: 4px solid #fe8d17; padding: 15px; margin: 15px 0;">
              <h3 style="margin-top: 0; color: #fe8d17;">Détails du message</h3>
              <p><strong>Nom :</strong> ${data.name}</p>
              <p><strong>Email :</strong> ${data.email}</p>
              ${data.phone ? `<p><strong>Téléphone :</strong> ${data.phone}</p>` : ''}
              <p><strong>Sujet :</strong> ${data.subject}</p>
              <p><strong>Date :</strong> ${new Date().toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            </div>

            <div style="background-color: #f0f0f0; padding: 15px; margin: 15px 0; border-radius: 5px;">
              <h4 style="margin-top: 0;">Message :</h4>
              <p style="white-space: pre-wrap;">${data.message}</p>
            </div>

            <p>Connectez-vous à votre interface d'administration pour répondre à ce message.</p>
          </div>
          <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
            <p>&copy; ${new Date().getFullYear()} ${tenantConfig.name}. Tous droits réservés.</p>
            <p>Notification automatique - Ne pas répondre à cet email.</p>
          </div>
        </div>
      `;

      const adminNotificationText = `
Nouveau message de contact reçu

Détails du message :
- Nom : ${data.name}
- Email : ${data.email}
${data.phone ? `- Téléphone : ${data.phone}` : ''}
- Sujet : ${data.subject}
- Date : ${new Date().toLocaleDateString('fr-FR')}

Message :
${data.message}

Connectez-vous à votre interface d'administration pour répondre à ce message.
      `;

      // Appel à la fonction Edge pour envoyer la notification admin
      const { VITE_SUPABASE_URL } = import.meta.env;
      const edgeFunctionUrl = `${VITE_SUPABASE_URL}/functions/v1/send-email`;

      // Utiliser la clé d'API Supabase pour l'authentification (comme pour les OTP)
      const { VITE_SUPABASE_KEY } = import.meta.env;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${VITE_SUPABASE_KEY}`
        },
        body: JSON.stringify({
          to: tenantConfig.email,
          toName: `Administrateur ${tenantConfig.name}`,
          subject: `[${tenantConfig.name}] Nouveau message de contact - ${data.subject}`,
          htmlContent: adminNotificationHtml,
          textContent: adminNotificationText,
          fromEmail: tenantConfig.config?.email2 || tenantConfig.email,
          fromName: `${tenantConfig.name} - Contact`,
          messageId: 0
        })
      });

      const result = await response.json();
      console.log('Notification admin envoyée:', result);

      if (!response.ok || !result.success) {
        console.error('Erreur lors de l\'envoi de la notification admin:', result.error);
        // Ne pas faire échouer l'ensemble si la notification échoue
      }
    } catch (notificationError) {
      console.error('Erreur lors de l\'envoi de la notification admin:', notificationError);
      // Ne pas faire échouer l'ensemble si la notification échoue
    }

    return { success: true };
  } catch (error) {
    console.error('Erreur:', error);
    return { success: false, error };
  }
};

export const sendReplyEmail = async (params: EmailParams): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Envoi d\'email à:', params.to);

    // Récupérer la configuration du tenant
    const tenantConfig = await getTenantConfig();

    // Préparer le contenu HTML si non fourni
    const htmlContent = params.htmlContent || `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenantConfig.logo}" alt="${tenantConfig.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Réponse à votre message</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${params.name},</p>
          <p>Merci d'avoir contacté notre service. Voici notre réponse :</p>
          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0;">
            ${params.replyText.replace(/\n/g, '<br>')}
          </div>
          <p>Si vous avez d'autres questions, n'hésitez pas à nous contacter à nouveau.</p>
          <p>Cordialement,<br>L'équipe de ${tenantConfig.name} </p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenantConfig.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    // Préparer le contenu texte
    const textContent = `
Bonjour ${params.name},

Merci d'avoir contacté ${tenantConfig.name}. Voici notre réponse :

${params.replyText}

Si vous avez d'autres questions, n'hésitez pas à nous contacter à nouveau.

Cordialement,
L'équipe de ${tenantConfig.name}
    `;

    // Appel à la fonction Edge Supabase pour l'envoi d'email
    const { VITE_SUPABASE_URL } = import.meta.env;
    const edgeFunctionUrl = `${VITE_SUPABASE_URL}/functions/v1/send-email`;

    console.log('URL de la fonction Edge:', edgeFunctionUrl);

    const senderType = params.sender ? String(params.sender) : undefined;
    const senderInfo = await getSenderInfo(senderType);
    const tenant = await getTenantConfig();

    console.log('Informations d\'expédition:', {
      to: params.to,
      subject: params.subject,
      fromEmail: senderInfo.email,
      fromName: senderInfo.name
    });

    try {
      // Obtenir la session utilisateur actuelle
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Aucune session utilisateur active');
      }

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          to: params.to,
          toName: params.name,
          subject: params.subject,
          htmlContent,
          textContent,
          fromEmail: senderInfo.email,
          fromName: senderInfo.name,
          messageId: params.messageId
        })
      });

      const result = await response.json();
      console.log('Réponse de la fonction Edge:', {
        status: response.status,
        statusText: response.statusText,
        result
      });

      if (!response.ok || !result.success) {
        throw new Error(result.error || `Erreur HTTP ${response.status}: ${response.statusText}`);
      }

      return result;
    } catch (error) {
      console.error('Erreur lors de l\'appel à la fonction Edge:', error);
      throw error;
    }

    // Mettre à jour le message dans la base de données si un ID est fourni
    if (params.messageId) {
      await supabase
        .from('contact_messages')
        .update({
          replied: true,
          reply_text: textContent,
          reply_date: new Date().toISOString()
        })
        .eq('id', params.messageId);
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email' };
  }
};

export const sendOtpEmail = async (data: OtpEmailData): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Envoi d\'email OTP à:', data.to);

    // Récupérer les informations du tenant
    const tenant = await getTenantConfig();

    // Appel à la fonction Edge Supabase pour l'envoi d'email
    const { VITE_SUPABASE_URL } = import.meta.env;
    const edgeFunctionUrl = `${VITE_SUPABASE_URL}/functions/v1/send-email`;

    // Utiliser la clé d'API Supabase pour l'authentification
    const { VITE_SUPABASE_KEY } = import.meta.env;

    // Préparer le contenu texte
    const textContent = `
Bonjour ${data.name},

Voici votre code de vérification pour accéder à vos commandes : ${data.otpCode}

Ce code est valable pendant 10 minutes.

Si vous n'avez pas demandé ce code, vous pouvez ignorer cet email.

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenant.logo}" alt="${tenant.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Code de vérification pour vos commandes</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${data.name},</p>
          <p>Voici votre code de vérification pour accéder à vos commandes :</p>

          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0; text-align: center;">
            <h1 style="font-size: 32px; margin: 0; color: #000;">${data.otpCode}</h1>
          </div>

          <p>Ce code est valable pendant 10 minutes.</p>
          <p>Si vous n'avez pas demandé ce code, vous pouvez ignorer cet email.</p>
          <p>Cordialement,<br>L'équipe de ${tenant.name}</p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${VITE_SUPABASE_KEY}`
      },
      body: JSON.stringify({
        to: data.to,
        toName: data.name,
        subject: `Code de vérification ${tenant.name}`,
        htmlContent,
        textContent,
        fromEmail: tenant.email,
        fromName: tenant.name,
        messageId: 0
      })
    });

    const result = await response.json();
    console.log('Réponse de la fonction Edge:', result);

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email OTP:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email OTP' };
  }
};

export const sendTicketEmail = async (data: TicketEmailData): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Envoi d\'email de billet à:', data.to);

    // Appel à la fonction Edge Supabase pour l'envoi d'email
    const { VITE_SUPABASE_URL } = import.meta.env;
    const edgeFunctionUrl = `${VITE_SUPABASE_URL}/functions/v1/send-email`;

    // Récupérer le token d'authentification
    const { data: { session } } = await supabase.auth.getSession();

    // Préparer le contenu HTML du billet
    const ticketHtml = data.qrCodes.map((qrCode, index) => `
      <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #f9f9f9;">
        <h3 style="margin-top: 0;">Billet #${index + 1}</h3>
        <p><strong>Code QR:</strong> ${qrCode}</p>
        <p><strong>Participant:</strong> ${data.name}</p>
        <div style="display: flex; flex-direction: column; align-items: center; margin-bottom: 15px;">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrCode)}" alt="QR Code" style="width: 150px; height: 150px; margin-bottom: 15px;"/>
          <a href="https://${window.location.hostname}/tickets/${qrCode}" style="display: inline-block; background-color: #f0c14b; color: #111; text-decoration: none; padding: 10px 15px; border-radius: 4px; margin-bottom: 10px; font-weight: bold;">Voir ce ticket</a>
        </div>
      </div>
    `).join('');

    const tenant = await getTenantConfig();

    // Préparer le contenu texte
    const textContent = `
Bonjour ${data.name},

Merci pour votre achat ! Voici vos tickets pour l'événement ${data.eventName} qui aura lieu le ${data.eventDate} à ${data.eventLocation}.

Détails de la commande :
- Type de ticket : ${data.ticketType}
- Prix unitaire : ${data.ticketPrice} FCFA
- Quantité : ${data.ticketQuantity}
- Code de commande : ${data.orderCode}

Veuillez présenter ce ticket (version imprimée ou sur votre téléphone) lors de votre arrivée à l'événement.

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background-color: #000; padding: 20px; text-align: center;">
          <img src="${tenant.logo}" alt="${tenant.name} Logo" style="max-width: 150px; margin-bottom: 15px;">
          <h2 style="margin: 0; color: #fff;">Vos tickets pour ${data.eventName}</h2>
        </div>
        <div style="padding: 20px; background-color: #fff; color: #333;">
          <p>Bonjour ${data.name},</p>
          <p>Merci pour votre achat ! Voici vos tickets pour l'événement <strong>${data.eventName}</strong> qui aura lieu le <strong>${data.eventDate}</strong> à <strong>${data.eventLocation}</strong>.</p>

          <div style="background-color: #f9f9f9; border-left: 4px solid #000; padding: 15px; margin: 15px 0;">
            <h3 style="margin-top: 0;">Détails de la commande</h3>
            <p><strong>Type de ticket :</strong> ${data.ticketType}</p>
            <p><strong>Prix unitaire :</strong> ${data.ticketPrice} FCFA</p>
            <p><strong>Quantité :</strong> ${data.ticketQuantity}</p>
            <p><strong>Code de commande :</strong> ${data.orderCode}</p>
          </div>

          <div style="margin-top: 20px;">
            <h3>Vos tickets</h3>
            ${ticketHtml}
            <div style="text-align: center; margin-top: 20px; margin-bottom: 20px;">
              <a href="https://${window.location.hostname}/my-tickets" style="display: inline-block; background-color: #333; color: #fff; text-decoration: none; padding: 12px 20px; border-radius: 4px; font-weight: bold;">Voir tous mes tickets</a>
            </div>
          </div>

          <p>Veuillez présenter ce ticket (version imprimée ou sur votre téléphone) lors de votre arrivée à l'événement.</p>
          <p>Cordialement,<br>L'équipe de ${tenant.name}</p>
        </div>
        <div style="background-color: #333; color: #fff; padding: 15px; text-align: center; font-size: 12px;">
          <p>&copy; ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
          <p>Ceci est un email automatique, merci de ne pas y répondre directement.</p>
        </div>
      </div>
    `;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        to: data.to,
        toName: data.name,
        subject: `Vos tickets pour ${data.eventName}`,
        htmlContent,
        textContent,
        fromEmail: tenant.email,
        fromName: tenant.name,
        messageId: 0
      })
    });

    const result = await response.json();
    console.log('Réponse de la fonction Edge:', result);

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Erreur lors de l\'envoi de l\'email de billet:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'envoi de l\'email de billet' };
  }
};